import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { ArrowLeft, Sparkles, ExternalLink, Settings2 } from "lucide-react";
import { money } from "@/components/marketplace/shared";
import { StatusPill, fmtDate } from "@/pages/MyFeatured";

const TYPES = { product: "Produtos", service: "Serviços", business: "Negócios", event: "Eventos" };
const STATUSES = ["", "active", "pending_payment", "expired", "cancelled", "refunded"];

export default function AdminFeatures() {
  const [tab, setTab] = useState("list");
  return (
    <div className="page fade-up" data-testid="admin-features-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Admin</Link>
      <h1 className="font-display text-2xl font-extrabold mb-3 flex items-center gap-2"><Sparkles size={20} /> Destaques pagos</h1>
      <div className="flex gap-2 mb-4">
        <button data-testid="admin-featured-tab-list" onClick={() => setTab("list")} className="chip" data-active={tab === "list"}>Destaques</button>
        <button data-testid="admin-featured-tab-prices" onClick={() => setTab("prices")} className="chip" data-active={tab === "prices"}><Settings2 size={12} className="inline mr-1" />Preços de destaque</button>
      </div>
      {tab === "list" ? <FeaturedList /> : <PricesEditor />}
    </div>
  );
}

function FeaturedList() {
  const { confirm, prompt } = useConfirm();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/featured/list", { params: status ? { status } : {} }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (f, action) => {
    let body = {};
    if (action === "refund") {
      if (!(await confirm({ title: `Reembolsar ${money(f.featured_amount_minor, "GBP")} a ${f.owner_email}?`, description: "Reembolso total via Stripe. O destaque termina de imediato e perde a prioridade.", confirmLabel: "Reembolsar", tone: "danger" }))) return;
    } else if (action === "cancel") {
      if (!(await confirm({ title: "Cancelar este destaque?", description: "Não executa reembolso. O conteúdo perde a prioridade de imediato.", confirmLabel: "Cancelar destaque", tone: "danger" }))) return;
    } else {
      const v = await prompt({ title: "Prolongar manualmente", description: "Número de dias a acrescentar (1–90), sem pagamento.", placeholder: "7", inputMode: "numeric", required: true, confirmLabel: "Prolongar" });
      if (v == null) return;
      body = { days: parseInt(v, 10) || 0, reason: "admin" };
    }
    setBusy(f.id);
    try { await api.post(`/admin/featured/${f.id}/${action}`, body); toast.success(action === "refund" ? "Reembolso executado" : action === "cancel" ? "Destaque cancelado" : "Destaque prolongado"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <>
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-3">
        {STATUSES.map((s) => <button key={s || "all"} data-testid={`admin-featured-filter-${s || "all"}`} onClick={() => setStatus(s)} className="chip whitespace-nowrap" data-active={status === s}>{s ? (s === "pending_payment" ? "Pendentes" : s) : "Todos"}</button>)}
      </div>
      {rows.length === 0 && <p className="text-sm text-zinc-500" data-testid="admin-featured-empty">Nenhum destaque.</p>}
      <div className="space-y-2">
        {rows.map((f) => (
          <div key={f.id} data-testid={`admin-featured-${f.id}`} className="bg-white rounded-xl border border-zinc-200 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate"><Link to={f.path} className="hover:underline">{f.target_name}</Link> {f.merged_into && <span className="text-[10px] text-zinc-500 font-normal">(prolongamento)</span>}</div>
                <div className="text-[11px] text-zinc-500 truncate">{f.type_label} · {f.featured_plan}d · {money(f.featured_amount_minor, "GBP")} · {f.owner_email}</div>
                <div className="text-[11px] text-zinc-400 mt-1">Início: {fmtDate(f.featured_started_at)} · Fim: {fmtDate(f.featured_ends_at)}{f.featured_status === "active" && ` · ${f.days_remaining} dias restantes`}</div>
                <div className="text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap">
                  {f.featured_payment_intent_id ? <a href={`https://dashboard.stripe.com/${f.stripe_mode === "test" ? "test/" : ""}payments/${f.featured_payment_intent_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">{f.featured_payment_intent_id} <ExternalLink size={9} /></a> : "sem pagamento"}
                  {f.metrics && <span>· {f.metrics.impression || 0} imp · {f.metrics.click || 0} cliques</span>}
                </div>
              </div>
              <StatusPill status={f.featured_status} testid={`admin-featured-status-${f.id}`} />
            </div>
            {(f.featured_status === "active" || f.featured_status === "pending_payment") && (
              <div className="flex gap-2 mt-2 pt-2 border-t border-zinc-100">
                {f.featured_status === "active" && f.featured_payment_intent_id && <button disabled={busy === f.id} data-testid={`admin-featured-refund-${f.id}`} onClick={() => act(f, "refund")} className="flex-1 py-2 rounded-lg text-xs font-semibold bg-[#c8102e] text-white disabled:opacity-50">Reembolsar</button>}
                <button disabled={busy === f.id} data-testid={`admin-featured-cancel-${f.id}`} onClick={() => act(f, "cancel")} className="flex-1 py-2 rounded-lg text-xs font-semibold bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50">Cancelar</button>
                {f.featured_status === "active" && !f.merged_into && <button disabled={busy === f.id} data-testid={`admin-featured-extend-${f.id}`} onClick={() => act(f, "extend")} className="flex-1 py-2 rounded-lg text-xs font-semibold bg-black text-white disabled:opacity-50">Prolongar</button>}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function PricesEditor() {
  const [prices, setPrices] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/featured/prices").then((r) => setPrices(r.data.prices)).catch(() => toast.error("Não foi possível carregar os preços")); }, []);
  if (!prices) return <p className="text-sm text-zinc-500">A carregar…</p>;

  const upd = (t, i, k, v) => setPrices((p) => ({ ...p, [t]: p[t].map((row, j) => (j === i ? { ...row, [k]: v } : row)) }));
  const save = async () => {
    setBusy(true);
    try {
      const clean = Object.fromEntries(Object.entries(prices).map(([t, rows]) => [t, rows.map((r) => ({ days: parseInt(r.days, 10) || 0, amount_minor: Math.round(parseFloat(String(r.amount_minor_gbp ?? r.amount_minor / 100).replace(",", ".")) * 100) || 0 }))]));
      const r = await api.put("/admin/featured/prices", { prices: clean });
      setPrices(r.data.prices); toast.success("Preços atualizados");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="admin-featured-prices">
      <p className="text-xs text-zinc-500">Preços em GBP por tipo de conteúdo e duração. Aplicam-se a novas compras; destaques já pagos não mudam.</p>
      {Object.keys(TYPES).map((t) => (
        <div key={t} className="bg-white rounded-xl border border-zinc-200 p-3">
          <div className="font-bold text-sm mb-2">{TYPES[t]}</div>
          <div className="grid grid-cols-3 gap-2">
            {(prices[t] || []).map((row, i) => (
              <div key={i} className="rounded-lg bg-zinc-50 border border-zinc-100 p-2 text-xs space-y-1">
                <label className="block text-[10px] text-zinc-500">Dias
                  <input data-testid={`price-days-${t}-${i}`} type="number" min="1" max="365" value={row.days} onChange={(e) => upd(t, i, "days", e.target.value)} className="w-full mt-0.5 px-2 py-1 rounded border border-zinc-200 bg-white" />
                </label>
                <label className="block text-[10px] text-zinc-500">Preço £
                  <input data-testid={`price-amount-${t}-${i}`} type="text" inputMode="decimal" value={row.amount_minor_gbp ?? (row.amount_minor / 100).toFixed(2)} onChange={(e) => upd(t, i, "amount_minor_gbp", e.target.value)} className="w-full mt-0.5 px-2 py-1 rounded border border-zinc-200 bg-white" />
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}
      <button data-testid="admin-featured-prices-save" disabled={busy} onClick={save} className="w-full py-3 rounded-xl bg-black text-white text-sm font-bold hover:bg-[#c8102e] transition-colors disabled:opacity-50">Guardar preços</button>
    </div>
  );
}
